import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, computed, ElementRef, OnDestroy, OnInit, signal, viewChild, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, ActivationStart, Router, RouterOutlet } from '@angular/router';
import { filterInputs } from '../../data/filter-inputs';
import { ButtonComponent } from '../../shared/components/button/button.component';
import { FiltersComponent } from '../../shared/components/filters/filters.component';
import { StatisticsComponent } from '../../shared/components/statistics/statistics.component';
import { KeyDisplayNamePipe } from '../../shared/components/stepper/pipes/key-display-name.pipe';
import { UserAcountTypes } from '../../shared/enums/common';
import { RideAttentionStatus } from '../../shared/enums/rideAttentionStatus';
import { RideStatusType } from '../../shared/enums/rideStatusType';
import { IFilterInput } from '../../shared/interfaces/filter-input.interface';
import { FiltersService } from '../../shared/services/filters.service';
import { FireStoreService } from '../../shared/services/firestore.service';
import { RequestBuilderService } from '../../shared/services/request-builder.service';
import { SingleMapService } from '../../shared/services/single-map.service';
import { StateService } from '../../shared/services/state.service';
import { ToggleMenuService } from '../../shared/services/toggle-menu.service';
import { where } from 'firebase/firestore';
import moment from 'moment';
import { MenuItem, MessageService, TooltipOptions } from 'primeng/api';
import { BadgeModule } from 'primeng/badge';
import { SelectChangeEvent, SelectModule } from 'primeng/select';
import { DialogService } from 'primeng/dynamicdialog';
import { Menu, MenuModule } from 'primeng/menu';
import { SelectButtonChangeEvent, SelectButtonModule } from 'primeng/selectbutton';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { Subscription, take } from 'rxjs';
import { SendNotificationToAllComponent } from './components/send-notification-to-all/send-notification-to-all.component';
import { ActionDialogService } from './services/action-dialog.service';
import { MapRideServiceService } from './services/map-ride-service.service';
import { AuthService } from '../../core/auth/services/auth.service';
import { LoaderComponent } from '../../core/components/loader/loader.component';
import { EnvConfigService } from '../../shared/services/env-config.service';
import { UserRole } from '../../core/auth/enums/userRoles';
import { EmergencyAlertComponent } from '../../shared/components/emergency-alert/emergency-alert.component';
import { SendScheduledNotificationDialogComponent } from './components/send-scheduled-notification-dialog/send-scheduled-notification-dialog.component';
import { NotificationQueueDialogComponent } from './components/notification-queue-dialog/notification-queue-dialog.component';
import { RideActionsService } from './services/ride-actions.service';

const MODULES = [SelectModule, BadgeModule, TooltipModule, FormsModule, SelectButtonModule, CommonModule, ToastModule, MenuModule];

const COMPONENTS = [ButtonComponent, StatisticsComponent, FiltersComponent, EmergencyAlertComponent];

const PIPES = [KeyDisplayNamePipe];

@Component({
  selector: 'app-monitoring-center',
  standalone: true,
  imports: [RouterOutlet, ...COMPONENTS, ...MODULES, ...PIPES, LoaderComponent],
  providers: [MessageService, DialogService],
  templateUrl: './monitoring-center.component.html',
  styleUrl: './monitoring-center.component.scss',
})
export class MonitoringCenterComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly stateService = inject(StateService);
  private readonly requestBuilder = inject(RequestBuilderService);
  private readonly dialogService = inject(DialogService);
  private readonly mapRideService = inject(MapRideServiceService);
  private readonly filtersService = inject(FiltersService);
  private readonly actionDialogService = inject(ActionDialogService);
  private readonly toggleMenuService = inject(ToggleMenuService);
  private readonly firestoreService = inject(FireStoreService);
  private readonly singleMapService = inject(SingleMapService);
  private readonly authService = inject(AuthService);
  readonly env = inject(EnvConfigService);
  private readonly messageService = inject(MessageService);
  private readonly rideActionsService = inject(RideActionsService);

  todayDate: string = '';
  timezoneOptions = [
    { name: 'Device Local Timezone', id: 'localTimezone' },
    { name: 'Ride Local Timezone', id: 'RideTimezone' },
    { name: 'California Timezone (PST)', id: 'systemPstTimezone' },
  ];
  selectedTimezone: string = 'localTimezone';

  tooltipOptions: TooltipOptions = {
    showDelay: 150,
    tooltipEvent: 'hover',
    tooltipPosition: 'bottom',
  };

  isRunAsLibrary = this.env.getConfig().isRunAsLibrary;
  partnersMessagesCount = 0;
  guardiansMessagesCount = 0;
  conversationsSubscription!: Subscription;

  isPendingRides = signal<boolean>(false);
  pendingRidesCount = signal<number>(0);
  isLargeScreen = signal<boolean>(typeof globalThis.window !== 'undefined' && globalThis.innerWidth > 1200);

  statistics = signal<{ name: string; value: number; color: string }[]>([]);
  statusOptions: { value: number; label: string }[] = [];
  periodOptions: { value: number; label: string }[] = [];

  viewsOptions = [
    { id: 1, name: 'List', icon: 'icon-list-bullets' },
    { id: 2, name: 'Map', icon: 'icon-map-trifold' },
  ];
  selectedView = 1;
  isDetailsView = signal<boolean>(false);
  readonly filtersComponentRef = viewChild.required<FiltersComponent>('filtersComponent');
  currentRole = (localStorage.getItem('role') ?? '').toLowerCase();
  userRoles = UserRole;
  inputs: IFilterInput[] = [];
  updatedControls!: IFilterInput | IFilterInput[];
  subdriverAssignedSubscription!: Subscription;
  numberOfResults = computed(() => {
    const totalResults = this.filtersService.totalResults();
    return totalResults;
  });

  allClients = [];
  allPlaces = [];
  allStates = [];
  allStudents = [];
  periods = [];

  appSetting = this.authService.appSetting();

  // Reference to the map's hidden div in the template
  readonly persistentMapHostRef = viewChild.required<ElementRef<HTMLDivElement>>('persistentMapHost');

  menu = signal<MenuItem[]>([
    {
      label: 'Guardians',
      icon: 'icon-chats-circle',
      styleClass: 'xl:hidden',
      visible: this.appSetting.hasGuardianMessagingPermission,
      command: e => {
        this.openMessagingDialog('guardian', true);
      },
    },
    {
      label: 'Drivers',
      icon: 'icon-chats-circle',
      styleClass: 'xl:hidden',
      visible: this.appSetting.hasDriverMessagingPermission,
      command: e => {
        this.openMessagingDialog('contractor', true);
      },
    },
    {
      label: 'Notification Queue',
      icon: 'icon-bell',
      styleClass: 'xl:hidden',
      visible: this.appSetting.hasSendScheduledNotification,
      command: e => {
        this.openNotificationQueue();
      },
    },
    {
      label: 'Pending Rides',
      icon: 'icon-car',
      styleClass: 'xl:hidden ' + (this.isPendingRides() ? 'active-menu-item' : ''),
      visible: this.appSetting.hasPendingRidesPermission,
      command: e => {
        this.togglePendingRides();
      },
    },
    {
      label: 'Send notification to all contractors',
      visible: this.appSetting.hasSendNotificationToAll,
      command: e => {
        this.openSendNotificationDialog();
      },
    },
    {
      label: 'Send scheduled notification to all contractors',
      visible: this.appSetting.hasSendScheduledNotification,
      command: e => {
        this.openSendScheduledNotificationDialog();
      },
    },
    {
      label: 'Send mass notification to all guardians',
      visible: this.appSetting.hasMassNotificationToGuardians,
      command: e => {
        this.openGuardianMassNotificationDialog();
      },
    },
    {
      label: 'Mass ride cancellations',
      visible: this.appSetting.hasMassCancellation,
      command: e => {
        this.openMassCancelRidesDialog();
      },
    },
    {
      label: 'Cancel student ride',
      visible: this.appSetting.hasCancelStudentRide,
      command: e => {
        this.openCancelStudentRideDialog();
      },
    },
  ]);

  hasVisibleMenuItems = computed(() => {
    const items = this.menu();
    const isLargeScreen = this.isLargeScreen();

    return items.some(item => {
      if (item.visible === false) {
        return false;
      }

      // If item has 'xl:hidden' and we're on large screen, skip it
      if (item.styleClass?.includes('xl:hidden') && isLargeScreen) {
        return false;
      }

      return true;
    });
  });

  ngOnInit() {
    this.isPendingRides.set(this.stateService.getIsPendingRides());
    const defaultView = this.currentRole == UserRole.Client ? 2 : 1;
    this.selectedView = defaultView;
    this.getDate();
    const serverTimezone = localStorage.getItem('serverTimezone');
    if (!serverTimezone) {
      this.requestBuilder.get(`configurations/server-timezone`).subscribe(res => {
        localStorage.setItem('serverTimezone', res.message ?? '');
      });
    }
    this.getConversationsCount();
    this.subscribeToRouterEvents();
    this.getEquipmentFile();
    this.setupResizeListener();

    this.subdriverAssignedSubscription = this.actionDialogService.subdriverAssigned$.subscribe((shouldFetchData: boolean) => {
      if (shouldFetchData) {
        this.getContractorFilters();
      }
    });

    this.selectedTimezone = localStorage.getItem('selectedTimezone') ?? 'localTimezone';
    this.mapRideService.TimezoneDropdownValue.set(this.selectedTimezone);
    this.inputs = filterInputs.filter(input => this.appSetting[input.permissionKey ?? '']);
    this.activatedRoute.queryParams.subscribe(params => {
      this.selectedView = params['view'] ? Number(params['view']) : defaultView;
    });

    this.stateService.statistics$.subscribe(res => {
      if (this.currentRole === this.userRoles.Client) {
        this.statistics.set(res.filter(x => x.name !== 'attention'));
      } else {
        this.statistics.set(res);
      }
    });

    // Subscribe to pending rides count
    this.stateService.pendingRidesCount$.subscribe(count => {
      this.pendingRidesCount.set(count);
    });

    this.getFilters();
  }

  ngAfterViewInit(): void {
    // Initialize the map ONCE after the view/host element is ready
    this.singleMapService.initializeMap(this.persistentMapHostRef().nativeElement);
  }

  async getDate() {
    let twText = '';
    await this.filtersService.setDateOfService();
    const serviceDate = localStorage.getItem('serviceDate');
    if (moment().format('MM/DD/YYYY') != serviceDate) {
      twText = 'Tomorrow ';
    }
    this.todayDate = `${twText}${serviceDate}`;
  }

  togglePendingRides() {
    const newValue = !this.isPendingRides();
    this.isPendingRides.set(newValue);
    this.stateService.setIsPendingRides(newValue);

    // Handle timezone dropdown based on pending rides state
    if (newValue) {
      // Pending rides: set to ride local timezone and disable
      this.selectedTimezone = 'RideTimezone';
      localStorage.setItem('selectedTimezone', this.selectedTimezone);
      this.mapRideService.TimezoneDropdownValue.set(this.selectedTimezone);
    }
    // Note: Dropdown will be enabled/disabled via template [disabled] binding
  }

  subscribeToRouterEvents() {
    this.router.events.subscribe(event => {
      if (event instanceof ActivationStart) {
        this.isDetailsView.set(!!event.snapshot.routeConfig?.path?.includes(`ride-details`));
      }
    });
  }

  setupResizeListener() {
    if (typeof globalThis.window !== 'undefined') {
      globalThis.addEventListener('resize', () => {
        this.isLargeScreen.set(globalThis.innerWidth > 1200);
      });
    }
  }

  openMenu(event: Event, menu: Menu) {
    this.toggleMenuService.openMenu(menu, event);
  }

  getFilters() {
    if (this.appSetting['hasPeriodFilterPermission']) this.getPeriodsFilters();
    if (this.appSetting['hasRideIdFilterPermission']) this.getRideFilters();
    if (this.appSetting['hasServiceRegionFilterPermission']) this.getServiceRegionsFilters();
    if (this.appSetting['hasServiceAreaFilterPermission']) this.getServiceAreasFilters();
    if (this.appSetting['hasStatusFilterPermission']) this.getStatusesFilters();
    if (this.appSetting['hasPassengerFilterPermission'] || this.appSetting['hasCancelStudentRide']) this.getPassengersFilters();
    if (this.appSetting['hasPlaceFilterPermission'] || this.appSetting['hasMassNotificationToGuardians']) this.getPlacesFilters();
    if (this.appSetting['hasClientFilterPermission'] || this.appSetting['hasMultiClientFilterPermission'] || this.appSetting['hasMassNotificationToGuardians']) this.getClientsFilters();
    if (this.appSetting['hasDriverProviderFilterPermission']) this.getProviderFilters();
    if (this.appSetting['hasContractorFilterPermission']) this.getContractorFilters();
    if (this.appSetting['hasStateFilterPermission'] || this.appSetting['hasMassNotificationToGuardians']) this.getStates();
    if (this.appSetting['hasEquipmentsNeedFilterPermission']) this.getEquipmentNeeds();
    if (this.appSetting['hasEquipmentsFilterPermission']) this.getEquipments();
    this.getCancellationReasons(); // Always get Cancellation reasons lookups for displaying the cancellation reason details
  }

  openMessagingDialog(field: 'guardian' | 'contractor', allMessages: boolean) {
    this.actionDialogService.openMessagingDialog(this.dialogService, field, allMessages);
  }

  getRideFilters(): void {
    this.requestBuilder.get(`filters/ride-number`).subscribe({
      next: res => {
        const options = res.data?.map((item: any) => ({
          label: item.name,
          value: item.id,
        }));
        this.updateDropdownOptions('OriginalRideId', options);
      },
      error: err => {
        console.error('Error fetching Service Regions Filters:', err);
      },
    });
  }

  getStates(): void {
    this.requestBuilder.get(`filters/state`).subscribe({
      next: res => {
        this.allStates = res.data;
        const options = res.data?.map((item: any) => ({
          label: item.trim(),
          value: item,
        }));
        this.updateDropdownOptions('State', options);
      },
      error: err => {
        console.error('Error fetching state Filters:', err);
      },
    });
  }

  getEquipmentNeeds(): void {
    this.requestBuilder.get(`filters/ride-equipments-need`).subscribe({
      next: res => {
        const options = res.data?.map((item: any) => ({
          label: item.name.trim(),
          value: item.id,
        }));
        this.updateDropdownOptions('EquipmentsNeed', options);
      },
      error: err => {
        console.error('Error fetching Equipments Need Filters:', err);
      },
    });
  }

  getEquipments(): void {
    this.requestBuilder.get(`filters/ride-equipments-type`).subscribe({
      next: res => {
        const options = res.data?.map((item: any) => ({
          label: item.name.trim(),
          value: item.id,
        }));
        this.updateDropdownOptions('EquipmentsType', options);
      },
      error: err => {
        console.error('Error fetching Equipments Filters:', err);
      },
    });
  }

  getCancellationReasons(): void {
    this.requestBuilder.get(`rides/cancellation-reasons`).subscribe({
      next: res => {
        this.stateService.setCancellationReasonsLookup(res.data);
        const options = res.data?.map((item: any) => ({
          label: item.name.trim(),
          value: item.id,
        }));
        this.updateDropdownOptions('CancellationReasons', options);
      },
      error: err => {
        console.error('Error fetching Cancellation Reasons Filters:', err);
      },
    });
  }

  getEquipmentFile() {
    this.requestBuilder.get(`rides/equipment-files`).subscribe(res => {
      this.stateService.setEquipmentsFiles(res.data);
    });
  }

  getConversationsCount() {
    this.conversationsSubscription = this.firestoreService
      .listenToCollectionChanges(this.env.getConfig().conversationsCollection, [where('platform_staff_unread_messages_count', '>', 0)])
      .pipe(take(1))
      .subscribe(res => {
        const result = res.changes.reduce(
          (acc: any, item: any) => {
            if (item.data.app_user_account_type == UserAcountTypes.Partner) {
              acc.partnersMessagesCount += item.data.platform_staff_unread_messages_count;
            } else if (item.data.app_user_account_type == UserAcountTypes.Guardian) {
              acc.guardiansMessagesCount += item.data.platform_staff_unread_messages_count;
            }
            return acc;
          },
          { partnersMessagesCount: 0, guardiansMessagesCount: 0 },
        );

        this.partnersMessagesCount = result.partnersMessagesCount;
        this.guardiansMessagesCount = result.guardiansMessagesCount;
      });
  }

  getPeriodsFilters(): void {
    this.requestBuilder.get(`filters/Periods`).subscribe({
      next: res => {
        const options = res.data?.map((item: any) => ({
          label: item.name,
          value: item.id,
        }));
        this.periods = options;
        this.periodOptions = options;
        this.updateDropdownOptions('PeriodsId', options);
      },
      error: err => {
        console.error('Error fetching Service Regions Filters:', err);
      },
    });
  }

  getServiceRegionsFilters(): void {
    this.requestBuilder.get(`filters/service-regions`).subscribe({
      next: res => {
        const options = res.data?.map((item: any) => ({
          label: item.name.trim(),
          value: item.id,
        }));
        this.updateDropdownOptions('ServiceRegionId', options);
      },
      error: err => {
        console.error('Error fetching Service Regions Filters:', err);
      },
    });
  }
  getServiceAreasFilters(): void {
    this.requestBuilder.get(`filters/service-areas`).subscribe({
      next: res => {
        const transformedData = res.data?.reduce(
          (result: any, item: any) => {
            const existingGroup = result.find((group: any) => group.label === item.state);

            if (existingGroup) {
              existingGroup.items.push({
                label: item.name.trim(),
                value: item.id,
              });
            } else {
              result.push({
                label: item.state,
                items: [
                  {
                    label: item.name.trim(),
                    value: item.id,
                  },
                ],
              });
            }

            return result;
          },
          [] as Array<{
            label: string;
            value: number;
            items: Array<{ label: string; value: number }>;
          }>,
        );

        this.updateDropdownOptions('ServiceAreaId', transformedData);
      },
      error: err => {
        console.error('Error fetching Service area Filters:', err);
      },
    });
  }
  getStatusesFilters(): void {
    this.requestBuilder.get(`filters/statuses`).subscribe({
      next: res => {
        const options = res.data?.map((item: any) => ({
          label: item.name.trim(),
          value: item.id,
        }));
        this.statusOptions = options;
        this.updateDropdownOptions('StatusId', options);
      },
      error: err => {
        console.error('Error fetching statuses:', err);
      },
    });
  }
  getPassengersFilters(): void {
    this.requestBuilder.get(`filters/passengers`).subscribe({
      next: res => {
        const options = res.data?.map((item: any) => ({
          label: `${item.firstName.trim()} ${item.lastName.trim()}`,
          value: item.id,
        }));
        this.allStudents = options;
        this.updateDropdownOptions('PassengerId', options);
      },
      error: err => {
        console.error('Error fetching passengers Filters:', err);
      },
    });
  }

  getPlacesFilters(): void {
    this.requestBuilder.get(`filters/places`).subscribe({
      next: res => {
        this.allPlaces = res.data;
        const options = res.data?.map((item: any) => ({
          label: item.name.trim(),
          value: item.id,
        }));
        this.updateDropdownOptions('PlaceId', options);
      },
      error: err => {
        console.error('Error fetching passengers Filters:', err);
      },
    });
  }

  getContractorFilters(): void {
    this.requestBuilder.get(`filters/partners`).subscribe({
      next: res => {
        const options = res.data?.map((item: any) => ({
          value: item.id,
          label: item.firstName.trim() + ' ' + item.lastName.trim(),
        }));
        this.updateDropdownOptions('PartnersId', options);
      },
      error: err => {
        console.error('Error fetching passengers Filters:', err);
      },
    });
  }
  getProviderFilters(): void {
    this.requestBuilder.get(`filters/organization-states`).subscribe({
      next: res => {
        const transformedData = res.data?.reduce(
          (result: any, item: any) => {
            const existingGroup = result.find((group: any) => group.label === item.state);

            if (existingGroup) {
              existingGroup.items.push({
                label: item.name.trim(),
                value: item.organizationId,
              });
            } else {
              result.push({
                label: item.state.trim(),
                value: item.organizationStateId,
                items: [
                  {
                    label: item.name.trim(),
                    value: item.organizationId,
                  },
                ],
              });
            }

            return result;
          },
          [] as Array<{
            label: string;
            value: number;
            items: Array<{ label: string; value: number }>;
          }>,
        );
        this.updateDropdownOptions('Provider', transformedData);
      },
      error: err => {
        console.error('Error fetching provider Filters:', err);
      },
    });
  }

  getClientsFilters(): void {
    this.requestBuilder.get(`filters/clients`).subscribe({
      next: res => {
        this.allClients = res.data;
        const options = res.data?.map((item: any) => ({
          label: item.name.trim(),
          value: item.id,
        }));
        this.appSetting['hasClientFilterPermission'] ? this.updateDropdownOptions('ClientId', options) : this.updateDropdownOptions('ClientIds', options);
      },
      error: err => {
        console.error('Error fetching passengers Filters:', err);
      },
    });
  }

  updateDropdownOptions(controlName: string, newOptions: { label: string; value: any }[]): void {
    const filter = this.inputs.find(f => f.controlName === controlName);
    if (filter) {
      filter.options = newOptions;
    }
  }

  openNotificationQueue() {
    this.dialogService.open(NotificationQueueDialogComponent, {
      showHeader: false,
      width: '1000px',
      modal: true,
      styleClass: 'dispatch-portal-custom-dialog',
      breakpoints: {
        '1200px': '90vw',
      },
    });
  }

  openSendNotificationDialog() {
    this.dialogService.open(SendNotificationToAllComponent, {
      showHeader: false,
      width: '640px',
      modal: true,
      styleClass: 'dispatch-portal-custom-dialog',
      breakpoints: {
        '768px': '90vw',
      },
    });
  }

  openSendScheduledNotificationDialog() {
    const ref = this.dialogService.open(SendScheduledNotificationDialogComponent, {
      showHeader: false,
      width: '640px',
      modal: true,
      styleClass: 'dispatch-portal-custom-dialog',
      breakpoints: {
        '768px': '90vw',
      },
    })!;

    ref.onClose.subscribe((res: any) => {
      if (res?.confirmed) {
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: `Scheduled Notification Created successfully!`,
          life: 6000,
        });
      }
    });
  }

  openMassCancelRidesDialog() {
    this.rideActionsService.openMassCancelRidesDialog(this.dialogService, this.messageService);
  }

  openGuardianMassNotificationDialog() {
    this.rideActionsService.openGuardianMassNotificationDialog(this.dialogService, this.messageService, this.allClients, this.allPlaces, this.allStates);
  }

  openCancelStudentRideDialog() {
    this.rideActionsService.openCancelStudentRideDialog(this.dialogService, this.messageService, this.allStudents, this.periods, this.todayDate);
  }

  setTimezone(e: SelectChangeEvent) {
    this.selectedTimezone = e.value;
    localStorage.setItem('selectedTimezone', this.selectedTimezone);
    this.mapRideService.TimezoneDropdownValue.set(this.selectedTimezone);
  }

  setStatusFilter(statusTitle: string, filtersComponent: FiltersComponent) {
    if (!filtersComponent.filterForm.controls['StatusId']) {
      return;
    }
    let statusValue;

    if (statusTitle == 'scheduled') statusValue = this.statusOptions.find(s => s.value == RideStatusType.None) ?? null;
    if (statusTitle == 'in Progress') statusValue = this.statusOptions.find(s => s.value == RideStatusType.InProgress) ?? null;
    if (statusTitle == 'completed') statusValue = this.statusOptions.find(s => s.value == RideStatusType.Completed) ?? null;
    if (statusTitle == 'late Canceled') statusValue = this.statusOptions.find(s => s.value == RideStatusType.CanceledLate) ?? null;
    if (statusTitle == 'Early  Cancel  + Holiday') statusValue = this.statusOptions.find(s => s.value == RideStatusType.CanceledInAdvance) ?? null;
    if (statusTitle == 'attention') statusValue = this.statusOptions.find(s => s.value == RideAttentionStatus.DriverNotAssigned) ?? null;
    if (statusTitle == 'no Show') statusValue = this.statusOptions.find(s => s.value == RideStatusType.NoShow) ?? null;

    filtersComponent.filterForm.controls['StatusId'].setValue(statusValue ?? '');
    filtersComponent.submit();
  }

  updateListViewParam() {
    if (this.env.getConfig().isRunAsLibrary) {
      this.router.navigateByUrl(`/dispatch/monitoring-center/rides?view=${this.selectedView}`);
    } else {
      this.router.navigateByUrl(`/monitoring-center/rides?view=${this.selectedView}`);
    }
  }

  onViewChange(e: SelectButtonChangeEvent) {
    this.updateListViewParam();
  }

  navigateToRidesBoard() {
    if (this.env.getConfig().isRunAsLibrary) {
      this.router.navigateByUrl('/dispatch/rides-board');
    } else {
      this.router.navigateByUrl('/rides-board');
    }
  }

  Search(filters: any) {
    if (filters) {
      this.filtersService.triggerFilterAction(filters);
    } else {
      this.filtersComponentRef().submit();
    }
  }

  showError(error: string) {
    alert(error);
  }

  ngOnDestroy() {
    this.subdriverAssignedSubscription?.unsubscribe();
    this.conversationsSubscription?.unsubscribe();
    if (typeof globalThis.window !== 'undefined') {
      globalThis.removeEventListener('resize', this.setupResizeListener);
    }
  }
}
